import React, { useState, useEffect } from "react";
import { Wallet, TrendingUp, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { getPassiveIncomeSummary } from "../lib/api";

function fmtINR(v) {
  if (!v && v !== 0) return "₹--";
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function PassiveIncomeCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let abort = false;
    getPassiveIncomeSummary()
      .then(r => { if (!abort) setData(r.data); })
      .catch(() => {});
    return () => { abort = true; };
  }, []);

  if (!data || !data.total_annual_estimate) return null;

  const total = data.total_annual_estimate;
  const monthlyAvg = data.monthly_avg;
  const yieldPct = data.portfolio_yield_pct;
  const months = data.by_month || [];
  const maxAmt = Math.max(...months.map(m => m.amount), 1);

  return (
    <div className="bg-gradient-to-br from-emerald-500/[0.04] to-cyan-500/[0.04] border border-emerald-500/15 rounded-lg p-4" data-testid="passive-income-card">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 items-stretch">
        {/* Left: KPI summary */}
        <div className="flex flex-col justify-center md:border-r md:border-white/5 md:pr-4 space-y-2">
          <div className="flex items-center gap-1.5">
            <Wallet size={12} className="text-emerald-300" />
            <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Expected annual dividend income</span>
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild><Info size={10} className="text-[var(--text-muted)]/60 cursor-help" /></TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[280px] text-xs">
                  Sum of trailing-12-month dividend payouts × your current shares for every India holding. Assumes companies will repeat their dividend schedule next year (true for ~90% of listed firms).
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-3xl font-mono font-bold text-emerald-300" data-testid="passive-income-total">
            {fmtINR(total)}
          </div>
          <div className="flex items-center gap-3 text-[11px] font-mono text-[var(--text-muted)]">
            <span>≈ {fmtINR(monthlyAvg)}/mo avg</span>
            <span className="flex items-center gap-1">
              <TrendingUp size={10} className="text-emerald-400" />
              {yieldPct.toFixed(2)}% portfolio yield
            </span>
          </div>
          {data.top_payers?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/5">
              <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Top payers</div>
              <div className="space-y-0.5">
                {data.top_payers.slice(0, 3).map(p => (
                  <div key={p.symbol} className="flex items-center justify-between text-[11px] font-mono" data-testid={`top-payer-${p.symbol}`}>
                    <span className="text-white truncate">{p.symbol}</span>
                    <span className="text-emerald-300">{fmtINR(p.annual_payout)}/yr</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Right: monthly bars */}
        <div data-testid="monthly-dividend-bars">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-2">Forecast by month (based on TTM schedule)</div>
          <div className="grid grid-cols-12 gap-1 h-[100px] items-end">
            {months.map(m => {
              const heightPct = m.amount > 0 ? Math.max(4, (m.amount / maxAmt) * 100) : 0;
              return (
                <TooltipProvider key={m.month_num} delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col items-center gap-1 cursor-help group" data-testid={`month-bar-${m.month}`}>
                        <div className="w-full flex items-end justify-center" style={{ height: 80 }}>
                          {m.amount > 0 ? (
                            <div
                              className="w-full bg-gradient-to-t from-emerald-500/60 to-emerald-400/80 rounded-sm group-hover:from-emerald-400/80 group-hover:to-emerald-300 transition-colors"
                              style={{ height: `${heightPct}%` }}
                            />
                          ) : (
                            <div className="w-full h-[2px] bg-white/10 rounded-sm" />
                          )}
                        </div>
                        <span className="text-[10px] font-mono text-[var(--text-muted)] group-hover:text-white">{m.month}</span>
                      </div>
                    </TooltipTrigger>
                    {m.amount > 0 && (
                      <TooltipContent side="top" className="text-xs max-w-[220px]">
                        <div className="font-mono font-semibold text-emerald-300">{fmtINR(m.amount)}</div>
                        <div className="mt-1 space-y-0.5 text-[var(--text-secondary)]">
                          {(m.top_contributors || []).slice(0, 3).map((c, i) => (
                            <div key={i} className="flex justify-between gap-3 font-mono text-[10px]">
                              <span>{c.symbol}</span>
                              <span className="text-emerald-300">{fmtINR(c.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
